import React from "react";
import {
  AbsoluteFill,
  Audio,
  Composition,
  Easing,
  Img,
  interpolate,
  Sequence,
  staticFile,
  useCurrentFrame,
} from "remotion";

export const NixlevPromoComposition: React.FC = () => {
  return (
    <Composition
      id="NixlevPromo"
      component={NixlevVideo}
      durationInFrames={960} // 32.00s @ 30fps
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

// Colors
const BG_CREAM = "#F7F5F0";
const NIXLEV_GREEN = "#1B3B2B";
const NIXLEV_ACCENT = "#D4A346";
const TEXT_DARK = "#1F1E1D";
const TEXT_MUTED = "#6B6964";

export const NixlevVideo: React.FC = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: BG_CREAM,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: TEXT_DARK,
        overflow: "hidden",
      }}
    >
      {/* Voiceover Track */}
      <Audio src={staticFile("nixlev_voice.mp3")} />

      {/* BEAT 1: Anti-Hook (0 - 115) */}
      <Sequence from={0} durationInFrames={115}>
        <Beat1Hook />
      </Sequence>

      {/* BEAT 2: Pain Point (115 - 262) */}
      <Sequence from={115} durationInFrames={147}>
        <Beat2PainPoint />
      </Sequence>

      {/* BEAT 3: Reveal Nixlev (262 - 325) */}
      <Sequence from={262} durationInFrames={63}>
        <Beat3Reveal />
      </Sequence>

      {/* BEAT 4: Showcase Landing Page (325 - 540) */}
      <Sequence from={325} durationInFrames={215}>
        <Beat4LandingPage />
      </Sequence>

      {/* BEAT 5: QRIS Checkout (540 - 740) */}
      <Sequence from={540} durationInFrames={200}>
        <Beat5Checkout />
      </Sequence>

      {/* BEAT 6: Zero Hassle (740 - 840) */}
      <Sequence from={740} durationInFrames={100}>
        <Beat6NoHassle />
      </Sequence>

      {/* BEAT 7: CTA Outro (840 - 960) */}
      <Sequence from={840} durationInFrames={120}>
        <Beat7CTA />
      </Sequence>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 1 */
const Beat1Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.94], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const crossScale = interpolate(frame, [50, 62], [0, 1.2], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 60px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      {frame >= 50 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {/* Header Pill */}
      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: TEXT_MUTED,
          letterSpacing: 2,
        }}
      >
        BIO LINK CHECK
      </div>

      {/* Phone Mockup with boring links */}
      <div
        style={{
          position: "relative",
          transform: `scale(${scale})`,
          width: 580,
          height: 980,
          backgroundColor: "#FFFFFF",
          borderRadius: 48,
          border: "8px solid #2B2927",
          boxShadow: "0 30px 70px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "50px 36px",
          boxSizing: "border-box",
        }}
      >
        {/* Profile Circle */}
        <div
          style={{
            width: 110,
            height: 110,
            borderRadius: "50%",
            backgroundColor: "#E2DDD5",
            marginBottom: 20,
          }}
        />
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: TEXT_DARK,
            marginBottom: 36,
          }}
        >
          @creator_kamu
        </div>

        {/* 3 Boring Grey Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
            width: "100%",
          }}
        >
          <div
            style={{
              padding: "24px",
              backgroundColor: "#F0ECE4",
              borderRadius: 16,
              textAlign: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "#8C877D",
            }}
          >
            Tombol Link 1
          </div>
          <div
            style={{
              padding: "24px",
              backgroundColor: "#F0ECE4",
              borderRadius: 16,
              textAlign: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "#8C877D",
            }}
          >
            Tombol Link 2
          </div>
          <div
            style={{
              padding: "24px",
              backgroundColor: "#F0ECE4",
              borderRadius: 16,
              textAlign: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "#8C877D",
            }}
          >
            Hubungi WhatsApp
          </div>
        </div>

        {/* Big Red Ban Crossout Overlay */}
        {frame >= 50 && (
          <div
            style={{
              position: "absolute",
              top: "35%",
              left: "10%",
              width: "80%",
              transform: `scale(${crossScale}) rotate(-12deg)`,
              backgroundColor: "#DC2626",
              color: "#FFFFFF",
              padding: "16px 20px",
              borderRadius: 16,
              textAlign: "center",
              fontSize: 42,
              fontWeight: 900,
              boxShadow: "0 20px 40px rgba(220,38,38,0.4)",
              letterSpacing: 2,
            }}
          >
            ✖ STOP PAKAI INI!
          </div>
        )}
      </div>

      {/* Bottom Subtitle Card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Stop link bio yang cuma deretan tombol kaku.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 2 */
const Beat2PainPoint: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const mascotFrame = Math.floor(frame * 0.9) % 47;
  const numStr = mascotFrame < 10 ? `0${mascotFrame}` : `${mascotFrame}`;

  return (
    <AbsoluteFill
      style={{
        padding: "100px 60px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: TEXT_MUTED,
          letterSpacing: 2,
        }}
      >
        MASALAH UTAMA
      </div>

      {/* Analytics Card + Clawd Mascot */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transform: `scale(${scale})`,
        }}
      >
        <div
          style={{
            width: 240,
            height: 162,
            marginBottom: -20,
            zIndex: 10,
          }}
        >
          <Img
            src={staticFile(`mascots/laptop/f${numStr}.png`)}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <div
          style={{
            width: 780,
            backgroundColor: "#FFFFFF",
            borderRadius: 36,
            padding: "48px 50px",
            border: "2px solid #E5E0D5",
            boxShadow: "0 25px 60px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#DC2626",
              letterSpacing: 2,
              marginBottom: 10,
            }}
          >
            📉 TINGKAT KONVERSI ANJLOK
          </div>
          <div
            style={{
              fontSize: 78,
              fontWeight: 900,
              color: TEXT_DARK,
              lineHeight: 1,
            }}
          >
            0.8% Saja
          </div>
          <div
            style={{
              fontSize: 26,
              color: TEXT_MUTED,
              marginTop: 18,
              lineHeight: 1.4,
            }}
          >
            Pengunjung bingung harus klik apa, akhirnya keluar tanpa beli apa
            pun.
          </div>
        </div>
      </div>

      {/* Bottom Subtitle Card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Orang bingung mau beli apa, ujungnya kabur tanpa closing.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 3 */
const Beat3Reveal: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 20], [1.15, 0.95], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: NIXLEV_GREEN,
        padding: "100px 60px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        color: "#FFFFFF",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.5} />
      <Audio src={staticFile("click.mp3")} volume={0.8} />

      <div
        style={{
          transform: `scale(${scale})`,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
        }}
      >
        <div
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: NIXLEV_ACCENT,
            letterSpacing: 4,
            textTransform: "uppercase",
          }}
        >
          Solusi Praktis
        </div>
        <h1
          style={{
            fontSize: 140,
            fontWeight: 900,
            letterSpacing: -4,
            margin: 0,
            lineHeight: 1,
            color: "#FFFFFF",
          }}
        >
          NIXLEV
        </h1>
        <div
          style={{
            fontSize: 34,
            fontWeight: 600,
            color: "#D0DDD5",
            maxWidth: 750,
            lineHeight: 1.4,
          }}
        >
          Penawaranmu dalam satu halaman yang jelas.
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 4 */
const Beat4LandingPage: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 25], [1.1, 0.88], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const scrollY = interpolate(frame, [40, 180], [0, -320], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const mascotFrame = Math.floor(frame * 0.9) % 47;
  const numStr = mascotFrame < 10 ? `0${mascotFrame}` : `${mascotFrame}`;

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        LANDING PAGE 5 MENIT
      </div>

      {/* Browser Mockup with Landing Page inside */}
      <div
        style={{
          transform: `scale(${scale})`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 220,
            height: 148,
            marginBottom: -22,
            zIndex: 15,
          }}
        >
          <Img
            src={staticFile(`mascots/laptop/f${numStr}.png`)}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <div
          style={{
            width: 880,
            height: 820,
            backgroundColor: "#FFFFFF",
            borderRadius: 28,
            border: "2px solid #E5E0D5",
            boxShadow: "0 30px 70px rgba(0,0,0,0.12)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Browser Bar */}
          <div
            style={{
              height: 54,
              backgroundColor: "#242220",
              display: "flex",
              alignItems: "center",
              padding: "0 22px",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <div
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  backgroundColor: "#FF5F56",
                }}
              />
              <div
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  backgroundColor: "#FFBD2E",
                }}
              />
              <div
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  backgroundColor: "#27C93F",
                }}
              />
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: "#383532",
                borderRadius: 8,
                padding: "6px 16px",
                color: "#CCCCCC",
                fontSize: 16,
                fontFamily: "monospace",
              }}
            >
              nixlev.com/panduan-bisnis
            </div>
          </div>

          {/* Scrolling Web Page Content */}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                transform: `translateY(${scrollY}px)`,
                padding: "40px",
                display: "flex",
                flexDirection: "column",
                gap: 28,
              }}
            >
              {/* Product Badge */}
              <div
                style={{
                  alignSelf: "flex-start",
                  backgroundColor: "#EBF3EE",
                  color: NIXLEV_GREEN,
                  padding: "8px 18px",
                  borderRadius: 999,
                  fontSize: 18,
                  fontWeight: 800,
                }}
              >
                PRODUK DIGITAL EBOOK
              </div>

              {/* Product Title */}
              <h2
                style={{
                  fontSize: 48,
                  fontWeight: 900,
                  color: TEXT_DARK,
                  margin: 0,
                  lineHeight: 1.15,
                }}
              >
                Framework Bisnis Digital 2026
              </h2>

              {/* Price & Buy Button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#F7F5F0",
                  padding: "24px 30px",
                  borderRadius: 20,
                  border: "1px solid #E5E0D5",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      color: TEXT_MUTED,
                      textDecoration: "line-through",
                    }}
                  >
                    Rp 299.000
                  </div>
                  <div
                    style={{
                      fontSize: 38,
                      fontWeight: 900,
                      color: NIXLEV_GREEN,
                    }}
                  >
                    Rp 149.000
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: NIXLEV_GREEN,
                    color: "#FFFFFF",
                    padding: "16px 28px",
                    borderRadius: 14,
                    fontSize: 20,
                    fontWeight: 800,
                  }}
                >
                  Beli Sekarang ➜
                </div>
              </div>

              {/* Testimonials Block */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #EAE6DE",
                  padding: "24px",
                  borderRadius: 18,
                }}
              >
                <div style={{ color: "#EAB308", fontSize: 24 }}>★★★★★</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: TEXT_DARK,
                    marginTop: 8,
                  }}
                >
                  "Konversi naik 3x lipat sejak pakai landing page Nixlev."
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Landing page elegan yang bikin orang yakin buat beli.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 5 */
const Beat5Checkout: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const notifSlide = interpolate(frame, [45, 65], [-40, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      {frame >= 45 && <Audio src={staticFile("click.mp3")} volume={0.7} />}

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        CHECKOUT OTOMATIS
      </div>

      {/* Checkout Card with QRIS */}
      <div
        style={{
          transform: `scale(${scale})`,
          width: 780,
          backgroundColor: "#FFFFFF",
          borderRadius: 36,
          padding: "48px 44px",
          border: "2px solid #E5E0D5",
          boxShadow: "0 25px 60px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 26,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#EBF3EE",
            padding: "8px 22px",
            borderRadius: 999,
          }}
        >
          <span style={{ fontSize: 20 }}>⚡</span>
          <span
            style={{ fontSize: 20, fontWeight: 800, color: NIXLEV_GREEN }}
          >
            SISTEM QRIS INSTAN
          </span>
        </div>

        {/* QR Code Graphic Box */}
        <div
          style={{
            width: 260,
            height: 260,
            backgroundColor: "#F7F5F0",
            border: "3px dashed #CBD5E1",
            borderRadius: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 72 }}>📱</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: TEXT_MUTED }}>
            SCAN QRIS DISINI
          </div>
        </div>

        {/* Payment Partner Logos */}
        <div
          style={{
            display: "flex",
            gap: 16,
            fontSize: 18,
            fontWeight: 800,
            color: TEXT_MUTED,
          }}
        >
          <span>BCA</span> • <span>MANDIRI</span> • <span>GOPAY</span> •{" "}
          <span>OVO</span> • <span>DANA</span>
        </div>

        {/* Success Alert Floating Banner */}
        {frame >= 45 && (
          <div
            style={{
              transform: `translateY(${notifSlide}px)`,
              backgroundColor: NIXLEV_GREEN,
              color: "#FFFFFF",
              width: "100%",
              padding: "18px 24px",
              borderRadius: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 15px 35px rgba(27,59,43,0.35)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>✔</span>
              <span style={{ fontSize: 22, fontWeight: 700 }}>
                Pembayaran Rp 149.000 Diterima!
              </span>
            </div>
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: NIXLEV_ACCENT,
              }}
            >
              1 DETIK
            </span>
          </div>
        )}
      </div>

      {/* Subtitle */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Aktif sistem pembayaran otomatis QRIS semua bank & e-wallet.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 6 */
const Beat6NoHassle: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 20], [1.12, 0.94], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        SERBA MUDAH
      </div>

      <div
        style={{
          transform: `scale(${scale})`,
          display: "flex",
          flexDirection: "column",
          gap: 24,
          width: 820,
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFFF",
            padding: "36px 40px",
            borderRadius: 28,
            border: "1px solid #E5E0D5",
            boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#FEE2E2",
              color: "#DC2626",
              width: 58,
              height: 58,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 900,
            }}
          >
            ✕
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: TEXT_DARK }}>
            Gak Perlu Sewa Hosting Mahal
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#FFFFFF",
            padding: "36px 40px",
            borderRadius: 28,
            border: "1px solid #E5E0D5",
            boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#FEE2E2",
              color: "#DC2626",
              width: 58,
              height: 58,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 900,
            }}
          >
            ✕
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: TEXT_DARK }}>
            Gak Perlu Belajar Koding Ribet
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Gak perlu sewa hosting atau belajar koding.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 7 */
const Beat7CTA: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const mascotFrame = Math.floor(frame * 0.9) % 47;
  const numStr = mascotFrame < 10 ? `0${mascotFrame}` : `${mascotFrame}`;

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      <Sequence from={20} layout="none">
        <Audio src={staticFile("click.mp3")} volume={0.7} />
      </Sequence>

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 700,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        MULAI SEKARANG
      </div>

      {/* Big Clean CTA Card + Mascot */}
      <div
        style={{
          transform: `scale(${scale})`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 240,
            height: 162,
            marginBottom: -22,
            zIndex: 10,
          }}
        >
          <Img
            src={staticFile(`mascots/laptop/f${numStr}.png`)}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <div
          style={{
            width: 820,
            backgroundColor: "#FFFFFF",
            borderRadius: 36,
            padding: "54px 50px",
            border: "2px solid #E5E0D5",
            boxShadow: "0 30px 70px rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 28,
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: 68,
              fontWeight: 900,
              color: TEXT_DARK,
              margin: 0,
              letterSpacing: -2,
            }}
          >
            NIXLEV.COM
          </h2>
          <p
            style={{
              fontSize: 26,
              color: TEXT_MUTED,
              margin: 0,
              maxWidth: 600,
              lineHeight: 1.4,
            }}
          >
            Buat halaman penawaran produk digital & jasamu dalam 5 menit.
          </p>

          <div
            style={{
              backgroundColor: NIXLEV_GREEN,
              color: "#FFFFFF",
              width: "100%",
              padding: "26px",
              borderRadius: 22,
              fontSize: 28,
              fontWeight: 800,
              boxShadow: "0 15px 35px rgba(27,59,43,0.35)",
            }}
          >
            Buat Halaman Sekarang ➜
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        Bikin halaman penawaranmu di nixlev.com
      </div>
    </AbsoluteFill>
  );
};
